import React, { useEffect, useRef, useState } from "react";
import formatMoney from "/src/helpers/formatMoney";
import { useApi } from "/src/helpers/Hooks/useApi";

// Shared SecureTrading (TrustPayments) hosted-fields checkout used by Make-a-Payment, Add Car Hire
// and Add Excursion. Generates a JWT for `amount`, renders the card fields, and calls `onApproved`
// with the response JWT only when the gateway actually approves the payment.
const ST_STYLES = {
    "background-color-input": "#F5F8FB",
    "border-color-input": "#D2DEE5",
    "border-radius-input": "16px",
    "border-size-input": "1px",
    "color-input": "#5F6C6E",
    "font-family-input": "system-ui, sans-serif",
    "font-size-input": "18px",
    "line-height-input": "20px",
    "space-inset-input": "10px",
    "border-color-input-error": "#fb2c36",
    "color-input-error": "#fb2c36",
    "color-asterisk": "#FF0055",
    "color-label": "#1A1F1F",
    "font-family-label": "system-ui, sans-serif",
    "font-size-label": "14px",
    "font-weight-label": 500,
    "line-height-label": "20px",
    "color-error": "#fb2c36",
};

// `disabled` holds the Pay button back (e.g. until the terms are ticked); `disabledHint` explains why.
const SecureTradingCheckout = ({ amount, customerDetails, payButtonText, onApproved, disabled = false, disabledHint = null }) => {
    const api = useApi();
    const [token, setToken] = useState(null);
    const [error, setError] = useState("");
    const stRef = useRef(null);
    const containerRef = useRef(null);
    // The hosted fields are initialised once, so their submit callback would otherwise keep the
    // onApproved closure from that first render — stale by the time the customer pays (e.g. the
    // terms ticked after the card form mounted). Always call the latest one.
    const onApprovedRef = useRef(onApproved);
    onApprovedRef.current = onApproved;

    // Generate the payment token for this amount.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await api.generatePaymentToken(amount, customerDetails || {});
                if (cancelled) return;
                if (result?.token) setToken(result.token);
                else setError(result?.error || "We couldn't start the payment. Please try again.");
            } catch {
                setError("Something went wrong starting the payment. Please try again.");
            }
        })();
        return () => { cancelled = true; };
    }, [amount]);

    // Initialise the hosted card fields once we have a token.
    useEffect(() => {
        if (!token || containerRef.current == null || stRef.current) return;
        // eslint-disable-next-line no-undef
        const st = SecureTrading({
            jwt: token,
            animatedCard: true,
            submitOnSuccess: false,
            styles: ST_STYLES,
            livestatus: window.paymentMode,
            translations: { Pay: payButtonText || `Pay £${formatMoney(amount)}` },
            submitCallback: (resp) => {
                // Only continue when the gateway actually approved the payment.
                if (!resp || (resp.errorcode !== undefined && String(resp.errorcode) !== "0")) {
                    setError(`Payment was not completed (${resp?.errormessage || "declined"}). Please try a different card.`);
                    return;
                }
                onApprovedRef.current?.(resp.jwt);
            },
        });
        st.Components();
        stRef.current = st;
    }, [token]);

    return (
        <div ref={containerRef}>
            {/* Hosted fields fill the left column; the animated card sits alongside on wide screens
                and drops beneath on narrow ones. Never fixed widths — the iframes track their box. */}
            <form id="st-form" action="https://www.olympicholidays.com" method="POST">
                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_350px] gap-x-8 gap-y-4 items-center">
                    <div className="min-w-0">
                        <div id="st-card-number" className="[&>iframe]:!h-[90px] [&>iframe]:!w-full"></div>
                        <div id="st-expiration-date" className="[&>iframe]:!h-[90px] [&>iframe]:!w-full"></div>
                        <div id="st-security-code" className="[&>iframe]:!h-[90px] [&>iframe]:!w-full"></div>
                    </div>
                    <div className="w-full max-w-[350px] mx-auto lg:mx-0">
                        <div id="st-animated-card" className="[&>iframe]:!min-h-[230px] [&>iframe]:!w-full"></div>
                    </div>
                </div>
                <div className="mt-6 flex flex-col items-end gap-2">
                    <button type="submit" disabled={disabled}
                            className={`btn-primary w-full sm:w-auto sm:min-w-[220px] text-white ${disabled ? "!bg-[#aebfcf] cursor-not-allowed hover:!bg-[#aebfcf]" : "bg-emp hover:bg-emp_dark"}`}>
                        Confirm &amp; Pay
                    </button>
                    {disabled && disabledHint && <p className="text-xs text-content_3">{disabledHint}</p>}
                </div>
            </form>
            {error && <div className="bg-error_2 text-error text-sm rounded-lg px-4 py-3 mt-4">{error}</div>}
        </div>
    );
};

export default SecureTradingCheckout;
